import React, { type ReactElement, type ReactNode, useState, useRef, useMemo } from 'react'
import { StyledDataGrid, StyledTableWrapper } from './styles'
import { type GridAlignment, type GridRenderCellParams, type GridValidRowModel, type GridTreeNodeWithRender, type GridRowParams, type GridSortModel, type GridSortItem, type GridRowModel, type GridRowProps, type RowPropsOverrides, GridRow } from '@mui/x-data-grid'
import { useTheme } from '@mui/material/styles'
import { type Row, type CustomTableProps, TableModalAction, Actions, type ScopeType } from './types'
import TableTopBarActions from './TableTopBarActions'
import { generateColumns } from './generateColumns'
import CustomPagination from './CustomPagination'
import ActionsWithDropdown from './CellTypes/ActionsWithDropdown'
import GenericModal from '../GenericModal'
import NoDataFound from './NoDataFound'
import { Box, CircularProgress, Typography } from '@mui/material'
import { blue, grey } from '../../colors'
import { ButtonType } from '../GenericButton/styles'
import lodash from 'lodash'
import { useOptionalWarningHighlight } from './WarningHighlighterContext'
import { useOptionalRefs } from './useOptionalRefs'
import { CellWarningType } from './CellTypes/constant'
import { useAppSearchParams } from '../../hooks/useAppSearchParams'
import { toBeSortPayload } from './cdgUtils'
import TooltipRow from './TooltipRow'
import { translateUserFacingText } from '../../utils/userFacingTerminology'

const CustomTable = <RowType extends Row>({
  rows,
  columns,
  label,
  createActionTitle = '',
  createAction,
  topbarTitle,
  enableAllSelection = false,
  hideSelectedColumnCount = true,
  featureName = '',
  editAction,
  deleteAction,
  errorAction,
  archiveAction,
  reopenSurveyAction,
  closeSurveyAction,
  goLiveAction,
  updateTemplateRevisionAction,
  disabledRowActions,
  disabledRowActionTooltip,
  rowLevelWarningKeys,
  extraColumnProps,
  extraModalProps,
  onRowClick,
  onEditClick,
  onPreviewClick,
  onOptOutClick,
  onOptOutInstructorClick,
  customTopbarAction,
  createButtonProps,
  onSearch = () => { },
  hideSearch = false,
  getRowTooltip,
  clickableRows = false,
  hasActiveFilters = false,
  handlePageChange = () => { },
  handleRowsPerPageChange = () => { },
  loading = false,
  noDataComponentProps,
  list,
  overrides = {},
  headerOverrides = {},
  onSortModelChange,
  actionModalEntityDisplayField = 'title',
  createModalProps,
  editModalProps,
  deleteModalProps,
  errorModalProps,
  exportCsv,
  exportCsvLoading = false,
  exportButtonProps = {},
  params = {},
  setParams = () => { },
  archiveModalProps,
  reopenSurveyModalProps,
  showScopeToggle = false,
  scopeToggleTitle = '',
  topbarActionsDropdownMenu,
  hideActionsColumn = false,
  disableRowDoubleClick = false,
  hideRowEditAction = false,
  extraRowActions
}: CustomTableProps<RowType>): JSX.Element => {
  const theme = useTheme()
  const { searchParams, setUrlParams } = useAppSearchParams()

  const [selectedRow, setSelectedRow] = useState<Row | null>(null)
  const [selectedRowWithActions, setSelectedRowWithActions] = useState<any | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [searchText, setSearchText] = useState<string | null>(null)
  const [sortModel, setSortModel] = useState<GridSortModel>()
  const [actionModal, setActionModal] = useState<TableModalAction | null>(null)
  const previousActionModalRef = useRef<TableModalAction | null>(null)
  const { pagination } = list ?? {}
  const tableRows = rows ?? list?.data ?? []
  const tableColumns = columns ?? list?.keys ?? []
  const entityDisplayField = lodash.get(selectedRow, actionModalEntityDisplayField)
  const deleteModalTitle = entityDisplayField ? `Delete "${entityDisplayField}"?` : 'Delete'
  const archiveModalTitle = entityDisplayField ? `Archive "${entityDisplayField}"?` : 'Archive'
  const errorType = previousActionModalRef.current?.toLowerCase()
  const errorModalTitle = entityDisplayField ? `Cannot ${errorType as unknown as string} "${entityDisplayField}"` : 'Delete'
  const selectedScope: ScopeType = searchParams.get('scope') as ScopeType ?? (params?.scope as ScopeType)

  const warningHighlight = useOptionalWarningHighlight()
  const registerCellRef = warningHighlight?.registerCellRef

  const handleScopeChange = (scope: ScopeType): void => {
    const { pageNumber, ...restParams } = params
    setParams({
      ...restParams,
      scope,
      pageNumber: 1
    })
    setUrlParams({ scope })
  }

  const handleSearch = (value: string): void => {
    const { pageNumber, ...restParams } = params
    setParams({
      ...restParams,
      search: value
    })
    setSearchText(value)
    onSearch(value)
  }

  const closeActionModal = (): void => {
    setActionModal(null)
    previousActionModalRef.current = actionModal
  }

  const EditDeleteArchiveActionWithModal: any = {
    field: 'actions',
    type: 'actions',
    align: 'right' as GridAlignment,
    minWidth: 180,
    flex: 1,
    renderCell: (params: GridRenderCellParams<GridValidRowModel, any, any, GridTreeNodeWithRender>): React.ReactNode => {
      const rowActions = [
        TableModalAction.EDIT,
        TableModalAction.READ,
        TableModalAction.DELETE,
        TableModalAction.ARCHIVE,
        TableModalAction.GO_LIVE,
        TableModalAction.OPT_OUT,
        TableModalAction.REOPEN_SURVEY,
        TableModalAction.CLOSE_SURVEY,
        TableModalAction.UPDATE_TEMPLATE_REVISION,
        Actions.SURVEY_PREVIEW,
        Actions.OPT_OUT_INSTRUCTOR
      ].some(action => params?.row?.actions?.includes(action))
      if (!rowActions) {
        return <></>
      }
      return (
      <ActionsWithDropdown
          handleSelectedRow={() => { setSelectedRow(params.row.data); setSelectedRowWithActions(params.row) }}
          rowActions={params?.row?.actions}
          onEditClick={onEditClick}
          onPreviewClick={onPreviewClick}
          onOptOutClick={onOptOutClick}
          onOptOutInstructorClick={onOptOutInstructorClick}
          setActionModal={setActionModal}
          params={params}
          disabledRowActions={disabledRowActions}
          disabledRowActionTooltip={disabledRowActionTooltip}
          hideRowEditAction={hideRowEditAction}
          extraRowActions={extraRowActions}
        />
      )
    }
  }

  const generatedCols = useMemo(() => {
    if (!Array.isArray(tableColumns)) return []
    return generateColumns(tableColumns, extraColumnProps, overrides, setSelectedRow, setActionModal, headerOverrides, rowLevelWarningKeys)
  }, [tableColumns, extraColumnProps, overrides, setSelectedRow, setActionModal, headerOverrides, rowLevelWarningKeys])

  // Adds refs to cells if highlighting is enabled
  const preparedColumns = useOptionalRefs(generatedCols)

  const tableTopBarProps = {
    onSearch: hideSearch ? undefined : handleSearch,
    createButtonProps,
    customTopbarAction,
    label,
    title: topbarTitle ?? list?.header?.headerName,
    createActionTitle,
    list,
    searchText,
    exportCsv,
    exportCsvLoading,
    exportButtonProps,
    handleCreateActionModal: () => { setActionModal(TableModalAction.CREATE) },
    showScopeToggle,
    scopeToggleTitle,
    selectedScope,
    onScopeChange: handleScopeChange,
    topbarActionsDropdownMenu
  }

  // • Calculates table height dynamically based on the current page size
  // • Uses a fixed `rowHeight` and adds an extra row for the header
  const rowHeight = 50
  const cdgHeader = 56
  const setTableHeight = (): string => {
    const singlePagePadding = pagination?.numPages === 1 ? 12 : 0
    const pageSize = pagination?.pageSize ?? 5
    const tableHeight = pageSize * rowHeight + cdgHeader + singlePagePadding
    return `${tableHeight}px`
  }

  const handleSortModelChange = (newSortModel: GridSortModel): void => {
    setSortModel(newSortModel)

    setParams((prev: typeof params) => ({
      ...prev,
      sort: toBeSortPayload(newSortModel)
    }))

    setUrlParams({
      ...params,
      sort: toBeSortPayload(newSortModel)
    })

    if (newSortModel.length > 0) {
      void onSortModelChange?.(newSortModel[0])
    }
  }

  const cloneElementWithProps = (
    element: ReactElement<any> | undefined,
    props?: Record<string, any>
  ): ReactNode => {
    if (!element) return null
    const defaultProps = {
      handleCloseModal: closeActionModal,
      showErrorModal: () => { previousActionModalRef.current = actionModal; setActionModal(TableModalAction.ERROR) },
      setErrorMessage
    }
    return React.cloneElement(element, { ...defaultProps, ...props })
  }
  const onRowDoubleClick = (params: GridRowParams): void => {
    const editAction = params?.row?.actions?.includes(Actions.EDIT)
    const readAction = params?.row?.actions?.includes(Actions.READ)
    if (!editAction && !readAction) { return }
    onEditClick ? onEditClick(params.row) : ((editAction || readAction) && setActionModal(TableModalAction.EDIT))
    setSelectedRow(params.row.data)
    setSelectedRowWithActions(params.row)
  }
  const getRowClassName = (params: GridRowParams): string => {
    const warningMsg = params.row?.data?.warningMessage
    if (warningMsg) {
      const keys = Object.keys(warningMsg)
      for (const k of keys) {
        if (warningMsg[k]?.key === CellWarningType.WARNING) return 'row-warning-bg'
        if (warningMsg[k]?.key === CellWarningType.ERROR) return 'row-error-bg'
      }
    }
    return ''
  }
  const isRowClickable = (params.row?.actions?.length > 0 && params.row?.actions.includes(Actions.EDIT)) || onEditClick

  const tableRowWithUniqueId = useMemo(
    () =>
      tableRows.map((r, index) => ({
        ...r,
        __id: `${index}`
      })),
    [tableRows]
  )

  return (
    <>
      <StyledTableWrapper>
        <TableTopBarActions {...tableTopBarProps} />
        {
          !loading && tableRows?.length === 0 && <NoDataFound list={list} noDataComponentProps={noDataComponentProps} searchText={params?.search ?? searchText} hasActiveFilters={hasActiveFilters} />
        }
        {
          loading && <Box display='flex' justifyContent='center' alignItems='center' minHeight={theme.spacing(80)}>
            <CircularProgress thickness={4} value={2} sx={{ color: blue[500] }} />
          </Box>
        }
        {
          !loading && preparedColumns?.length > 0 && (tableRows?.length > 0) && tableRows.length > 0 &&
          <>
            <StyledDataGrid
              tableHeight={setTableHeight()}
              isRowSelectable={(params: GridRowParams) => params.row?.actions?.length > 0}
               getRowId={(row) => row.__id}
              columns={hideActionsColumn ? preparedColumns : [...preparedColumns, EditDeleteArchiveActionWithModal]}
              rows={tableRowWithUniqueId}
              checkboxSelection={enableAllSelection}
              hideFooterPagination={true}
              hideFooterSelectedRowCount={hideSelectedColumnCount}
              onRowClick={onRowClick}
              onRowDoubleClick={disableRowDoubleClick ? undefined : onRowDoubleClick}
              rowHeight={rowHeight}
              onSortModelChange={handleSortModelChange}
              sortingMode="server"
              sortModel={sortModel}
              getRowClassName={getRowClassName}
              disableColumnMenu
              isRowClickable={Boolean(isRowClickable) || clickableRows}
              autosizeOnMount={true}
              slots={{
                row: (props) => (
                  <TooltipRow
                    {...props}
                    list={list}
                    onEditClick={onEditClick}
                    isRowClickable={isRowClickable}
                    getRowTooltip={getRowTooltip}
                  />
                )
              }}
              disableVirtualization={!!registerCellRef && tableRows?.length < 200}
            />
            {pagination && (
              <CustomPagination
                {...pagination}
                handlePageChange={(pageNumber) => {
                  setParams({
                    ...params,
                    pageNumber
                  })
                  void handlePageChange(pageNumber)
                }}
                handleRowsPerPageChange={(pageSize) => {
                  setParams({
                    ...params,
                    pageSize
                  })
                  void handleRowsPerPageChange(pageSize)
                }}
              />
            )}
          </>
        }
        {/* CREATE MODAL  */}
        <GenericModal
          isOpen={actionModal === TableModalAction.CREATE}
          title={`Create ${featureName}`}
          onClose={() => { closeActionModal() }}
          maxWidth='sm'
          showCloseIcon={false}
          action={false}
          {...extraModalProps}
          {...createModalProps}
        >
          {cloneElementWithProps(createAction)}
        </GenericModal>
        {/* EDIT MODAL  */}
        <GenericModal
          isOpen={actionModal === TableModalAction.EDIT}
          title={`Edit ${featureName}`}
          onClose={() => { closeActionModal() }}
          maxWidth='sm'
          showCloseIcon={true}
          action={false}
          {...extraModalProps}
          {...editModalProps}
        >
          {cloneElementWithProps(editAction, { selectedRow, selectedRowWithActions })}
        </GenericModal>
        {/* DELETE MODAL  */}
        <GenericModal
          showCloseIcon={false}
          isOpen={actionModal === TableModalAction.DELETE}
          title={deleteModalTitle}
          onClose={() => { closeActionModal() }}
          maxWidth='sm'
          modalWidth={theme.spacing(136)}
          action={false}
          {...extraModalProps}
          {...deleteModalProps}
        >
          {cloneElementWithProps(deleteAction, { selectedRow })}
        </GenericModal>
        {/* Error MODAL  */}
        <GenericModal
          showCloseIcon={false}
          isOpen={actionModal === TableModalAction.ERROR}
          title={errorModalTitle}
          onClose={() => { closeActionModal() }}
          maxWidth='sm'
          modalWidth={theme.spacing(136)}
          action={true}
          hideConfirmButton
          destructiveButtonText='Close'
          destructiveButtonType={ButtonType.Primary}
          {...extraModalProps}
          {...errorModalProps}
        >
          {
            errorAction
              ? cloneElementWithProps(errorAction, { selectedRow })
              : <Typography fontSize={'14px'} color={grey[850]}>{errorMessage ? translateUserFacingText(errorMessage) : errorMessage}</Typography>
          }
        </GenericModal>
        {/* Archive MODAL  */}
        <GenericModal
          isOpen={actionModal === TableModalAction.ARCHIVE}
          title={archiveModalTitle}
          onClose={() => { closeActionModal() }}
          maxWidth='sm'
          modalWidth={theme.spacing(136)}
          action={false}
          {...extraModalProps}
          {...archiveModalProps}
        >
          {cloneElementWithProps(archiveAction, { selectedRow })}
        </GenericModal>
        {/* Reopen Survey Modal */}
        <GenericModal
          isOpen={actionModal === TableModalAction.REOPEN_SURVEY}
          title='Re-open Course Section Survey?'
          onClose={() => { closeActionModal() }}
          maxWidth='sm'
          modalWidth={theme.spacing(136)}
          action={false}
          {...extraModalProps}
          {...reopenSurveyModalProps}
        >
          {cloneElementWithProps(reopenSurveyAction, { selectedRow })}
        </GenericModal>
        {/* Close Survey Modal */}
        <GenericModal
          isOpen={actionModal === TableModalAction.CLOSE_SURVEY}
          title='Close Survey'
          onClose={() => { closeActionModal() }}
          maxWidth='sm'
          action={false}
          {...extraModalProps}
        >
          {cloneElementWithProps(closeSurveyAction, { selectedRow })}
        </GenericModal>
        {/* Go Live Modal */}
        <GenericModal
          isOpen={actionModal === TableModalAction.GO_LIVE}
          title='Go Live'
          onClose={() => { closeActionModal() }}
          maxWidth='sm'
          action={false}
          {...extraModalProps}
        >
          {cloneElementWithProps(goLiveAction, { selectedRow })}
        </GenericModal>
        {/* Update Template Revision Modal */}
        <GenericModal
          isOpen={actionModal === TableModalAction.UPDATE_TEMPLATE_REVISION}
          title='Update Template Revision'
          onClose={() => { closeActionModal() }}
          maxWidth='sm'
          action={false}
          {...extraModalProps}
        >
          {cloneElementWithProps(updateTemplateRevisionAction, { selectedRow })}
        </GenericModal>
      </StyledTableWrapper>
    </>
  )
}
export default React.memo(CustomTable)
